import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import {
  EmployeeCompensationFormValues,
  EmployeeCompensationLineFormValue,
} from "@/pages/employees/employee/components/employee-compensation-form.types.ts";
import { ApiCompensationComponent } from "@/types/compensation-component.types.ts";
import { getEmployeeCompensationPerTypes } from "@/types/employees/employee-compensation.types.ts";
import {
  Box,
  Button,
  ButtonIcon,
  FontAwesomeIcon,
  Input,
  Select,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface EmployeeCompensationLinesProps {
  fields: Array<EmployeeCompensationLineFormValue & { fieldKey: string }>;
  components: ApiCompensationComponent[];
  control: Control<EmployeeCompensationFormValues>;
  errors: FieldErrors<EmployeeCompensationFormValues>;
  onAdd: () => void;
  onDelete: (index: number) => void;
  onComponentChange: (index: number, componentId: number) => void;
  onAmountChange: (index: number) => void;
}

export function EmployeeCompensationLines({
  fields,
  components,
  control,
  errors,
  onAdd,
  onDelete,
  onComponentChange,
  onAmountChange,
}: EmployeeCompensationLinesProps) {
  const { t } = useTranslation();

  return (
    <>
      <Title header={t("core:compensation")} size="xs" paddingTop paddingBottom />
      <Stack gap="sm">
        {fields.map((field, index) => {
          const grossError = errors.lines?.[index]?.amount_gross?.message;
          const netError = errors.lines?.[index]?.amount_net?.message;
          const isAmountRequired =
            (field.amount_gross === null || field.amount_gross === "") &&
            (field.amount_net === null || field.amount_net === "") &&
            Boolean(grossError && netError);

          return (
            <Box key={field.fieldKey} variant="border" className="bg-dark/5 p-4">
              <Stack gap={"sm"}>
                <Controller
                  control={control}
                  name={`lines.${index}.compensation_component_id`}
                  render={({ field: componentField }) => (
                    <Select
                      {...componentField}
                      required
                      label={t("core:compensation_type")}
                      options={components}
                      emptyTitle={t("common:select")}
                      searchable
                      error={errors.lines?.[index]?.compensation_component_id?.message}
                      onChange={(componentId) => {
                        componentField.onChange(componentId);
                        onComponentChange(index, Number(componentId));
                      }}
                    />
                  )}
                />
                {field.compensation_component_id > 0 && (
                  <>
                    <Stack direction="horizontal" gap="md">
                      <Box className="w-full">
                        <Controller
                          control={control}
                          name={`lines.${index}.amount_net`}
                          render={({ field: amountField }) => (
                            <Input
                              {...amountField}
                              value={String(amountField.value ?? "")}
                              type="number"
                              inputMode="decimal"
                              label={t("core:compensation_line_net")}
                              className={isAmountRequired ? "[&>.py-2]:hidden" : undefined}
                              error={netError}
                              onChange={(event) => {
                                amountField.onChange(event);
                                onAmountChange(index);
                              }}
                            />
                          )}
                        />
                      </Box>
                      <Box className="w-full">
                        <Controller
                          control={control}
                          name={`lines.${index}.amount_gross`}
                          render={({ field: amountField }) => (
                            <Input
                              {...amountField}
                              value={String(amountField.value ?? "")}
                              type="number"
                              inputMode="decimal"
                              label={t("core:compensation_line_gross")}
                              className={isAmountRequired ? "[&>.py-2]:hidden" : undefined}
                              error={grossError}
                              onChange={(event) => {
                                amountField.onChange(event);
                                onAmountChange(index);
                              }}
                            />
                          )}
                        />
                      </Box>
                    </Stack>
                    {isAmountRequired && (
                      <Text className="text-danger text-sm">{t("errors:field_required")}</Text>
                    )}
                    <Stack direction="horizontal" gap="sm" items="center" justify={"between"}>
                      <Stack direction="horizontal" gap="sm" items="center">
                        <Controller
                          control={control}
                          name={`lines.${index}.per_type`}
                          render={({ field: periodField }) => (
                            <Select
                              {...periodField}
                              required
                              variant="button"
                              options={getEmployeeCompensationPerTypes()}
                              emptyTitle={t("common:period")}
                              error={errors.lines?.[index]?.per_type?.message}
                            />
                          )}
                        />
                        <Text>{t("core:compensation_line_currency_separator")}</Text>
                        <Controller
                          control={control}
                          name={`lines.${index}.currency_code`}
                          render={({ field: currencyField }) => (
                            <CurrencySelect
                              {...currencyField}
                              required
                              variant="button"
                              emptyTitle={t("common:currency")}
                              searchable
                              error={errors.lines?.[index]?.currency_code?.message}
                            />
                          )}
                        />
                      </Stack>
                      {fields.length > 1 && (
                        <ButtonIcon
                          variant="plain"
                          className="ml-auto"
                          onClick={() => onDelete(index)}
                          icon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                        />
                      )}
                    </Stack>
                  </>
                )}
                {!field.compensation_component_id && fields.length > 1 && (
                  <Stack direction="horizontal" justify="end">
                    <ButtonIcon
                      variant="plain"
                      onClick={() => onDelete(index)}
                      icon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                    />
                  </Stack>
                )}
              </Stack>
            </Box>
          );
        })}
        <Stack direction="horizontal">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            onClick={onAdd}
          >
            {t("core:compensation_add_line")}
          </Button>
        </Stack>
      </Stack>
    </>
  );
}
